import { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { config } from '../config';
import { getAuthToken } from '../utils/authToken';
import { fetchWithRetry } from '../utils/fetchWithRetry';
import { PROFILING_CONSENT_VERSION } from '../config/legalDocuments';

interface ProfilingConsent {
  version: string | null;
  acceptedAt: string | null;
  withdrawnAt: string | null;
  isActive: boolean;
}

export default function MarketingEmailsPage() {
  const { instance } = useMsal();
  const navigate = useNavigate();

  const [profilingConsent, setProfilingConsent] = useState<ProfilingConsent | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [message, setMessage] = useState('');
  const [showConfirmWithdraw, setShowConfirmWithdraw] = useState(false);

  useEffect(() => {
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadProfile = async () => {
    setIsLoading(true);
    try {
      const token = await getAuthToken(instance);
      if (!token) {
        setIsLoading(false);
        return;
      }

      const response = await fetchWithRetry(`${config.apiUrl}/getUserProfile`, {
        headers: {
          'X-Auth-Token': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setProfilingConsent(data.user.profilingConsent);
      }
    } catch (error) {
      console.error('Failed to load profile:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEnableProfiling = async () => {
    setIsProcessing(true);
    setMessage('');

    try {
      const token = await getAuthToken(instance);
      if (!token) {
        setMessage('Chyba autentifikácie');
        setIsProcessing(false);
        return;
      }

      const response = await fetchWithRetry(`${config.apiUrl}/acceptLegalDocument`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Auth-Token': `Bearer ${token}`
        },
        body: JSON.stringify({
          documentTypes: ['profiling'],
          versions: { profiling: PROFILING_CONSENT_VERSION }
        })
      });

      if (response.ok) {
        setMessage('Profilovací súhlas bol udelený');
        await loadProfile();
      } else {
        const error = await response.json();
        setMessage(error.message || 'Nepodarilo sa uložiť súhlas');
      }
    } catch (error) {
      console.error('Failed to enable profiling:', error);
      setMessage('Chyba pri ukladaní');
    } finally {
      setIsProcessing(false);
      setTimeout(() => setMessage(''), 4000);
    }
  };

  const handleWithdrawProfiling = async () => {
    setIsProcessing(true);
    setMessage('');
    setShowConfirmWithdraw(false);

    try {
      const token = await getAuthToken(instance);
      if (!token) {
        setMessage('Chyba autentifikácie');
        setIsProcessing(false);
        return;
      }

      const response = await fetchWithRetry(`${config.apiUrl}/withdrawProfilingConsent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Auth-Token': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setMessage('Profilovací súhlas bol odvolaný');
        await loadProfile();
      } else {
        const error = await response.json();
        setMessage(error.message || 'Nepodarilo sa odvolať súhlas');
      }
    } catch (error) {
      console.error('Failed to withdraw profiling:', error);
      setMessage('Chyba pri odvolávaní');
    } finally {
      setIsProcessing(false);
      setTimeout(() => setMessage(''), 4000);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString('sk-SK', {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Loading skeleton
  if (isLoading) {
    return (
      <div style={{ maxWidth: '600px', margin: '0 auto', padding: '20px 20px 100px 20px' }}>
        <div style={{ height: '20px', background: '#e5e7eb', borderRadius: '4px', width: '60px', marginBottom: '16px', animation: 'pulse 1.5s ease-in-out infinite' }} />
        <div style={{ height: '28px', background: '#e5e7eb', borderRadius: '4px', width: '80%', marginBottom: '12px', animation: 'pulse 1.5s ease-in-out infinite' }} />
        <div style={{ height: '16px', background: '#e5e7eb', borderRadius: '4px', width: '90%', marginBottom: '32px', animation: 'pulse 1.5s ease-in-out infinite' }} />
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px', marginBottom: '16px' }}>
          <div style={{ height: '16px', background: '#e5e7eb', borderRadius: '4px', width: '60%', marginBottom: '12px', animation: 'pulse 1.5s ease-in-out infinite' }} />
          <div style={{ height: '14px', background: '#e5e7eb', borderRadius: '4px', width: '80%', animation: 'pulse 1.5s ease-in-out infinite' }} />
        </div>
        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}</style>
      </div>
    );
  }

  const isActive = profilingConsent?.isActive ?? false;

  return (
    <div style={{ maxWidth: '600px', margin: '0 auto', padding: '20px 20px 100px 20px' }}>
      {/* Back button */}
      <button
        onClick={() => navigate('/profile')}
        style={{
          background: 'none',
          border: 'none',
          color: '#667eea',
          fontSize: '16px',
          cursor: 'pointer',
          padding: '0',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '4px'
        }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        Späť na profil
      </button>

      <h1 style={{ fontSize: '28px', fontWeight: 700, color: '#111827', marginBottom: '12px' }}>
        Profilovací súhlas
      </h1>

      <p style={{ fontSize: '16px', color: '#6b7280', lineHeight: '1.6', marginBottom: '24px' }}>
        Spravujte svoj súhlas s profilovaním a personalizovanými službami.
      </p>

      {/* Current status */}
      <div style={{
        background: isActive ? '#f0fdf4' : '#f9fafb',
        border: `1px solid ${isActive ? '#bbf7d0' : '#e5e7eb'}`,
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '24px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          <span style={{
            display: 'inline-block',
            width: '10px',
            height: '10px',
            borderRadius: '50%',
            backgroundColor: isActive ? '#22c55e' : '#9ca3af'
          }} />
          <span style={{ fontSize: '16px', fontWeight: 600, color: '#111827' }}>
            {isActive ? 'Aktívny' : 'Neaktívny'}
          </span>
        </div>
        {isActive && profilingConsent?.acceptedAt && (
          <p style={{ fontSize: '13px', color: '#6b7280', margin: 0 }}>
            Udelený: {formatDate(profilingConsent.acceptedAt)}
          </p>
        )}
        {!isActive && profilingConsent?.withdrawnAt && (
          <p style={{ fontSize: '13px', color: '#6b7280', margin: 0 }}>
            Odvolaný: {formatDate(profilingConsent.withdrawnAt)}
          </p>
        )}
      </div>

      {/* What profiling includes */}
      <div style={{
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '24px'
      }}>
        <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#111827', margin: '0 0 12px 0' }}>
          Čo zahŕňa profilovací súhlas
        </h3>
        <ul style={{ margin: 0, paddingLeft: '20px', color: '#4b5563', lineHeight: '2' }}>
          <li>Marketingové e-maily s tipmi a novinkami</li>
          <li>Mesačné prehľady vašich výdavkov (AI analýza)</li>
          <li>Sledovanie otvorení a kliknutí v e-mailoch pre lepšiu personalizáciu</li>
        </ul>
      </div>

      {/* Info box */}
      <div style={{
        background: '#eff6ff',
        border: '1px solid #bfdbfe',
        borderRadius: '8px',
        padding: '12px 16px',
        marginBottom: '24px'
      }}>
        <p style={{ fontSize: '14px', color: '#1e40af', margin: 0, lineHeight: '1.5' }}>
          <strong>Poznámka:</strong> Systémové e-maily (napr. o rozdelení účtu, potvrdenia)
          budete dostávať vždy, bez ohľadu na toto nastavenie.
        </p>
      </div>

      {/* Status message */}
      {message && (
        <div style={{
          marginBottom: '16px',
          padding: '12px',
          borderRadius: '8px',
          background: message.includes('udelený') || message.includes('odvolaný') ? '#d1fae5' : '#fee2e2',
          color: message.includes('udelený') || message.includes('odvolaný') ? '#059669' : '#dc2626',
          fontSize: '14px',
          fontWeight: 500
        }}>
          {message}
        </div>
      )}

      {/* Withdraw confirmation */}
      {showConfirmWithdraw && (
        <div style={{
          background: '#fef3c7',
          border: '1px solid #fbbf24',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '24px'
        }}>
          <p style={{ fontSize: '15px', color: '#92400e', margin: '0 0 16px 0', lineHeight: '1.5' }}>
            Naozaj chcete odvolať profilovací súhlas? Prestanete dostávať marketingové e-maily
            a mesačné prehľady.
          </p>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={handleWithdrawProfiling}
              disabled={isProcessing}
              style={{
                flex: 1,
                background: '#dc2626',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                padding: '12px',
                fontSize: '15px',
                fontWeight: 600,
                cursor: isProcessing ? 'not-allowed' : 'pointer'
              }}
            >
              {isProcessing ? 'Spracúvam...' : 'Áno, odvolať'}
            </button>
            <button
              onClick={() => setShowConfirmWithdraw(false)}
              disabled={isProcessing}
              style={{
                flex: 1,
                background: '#fff',
                color: '#374151',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '12px',
                fontSize: '15px',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Zrušiť
            </button>
          </div>
        </div>
      )}

      {/* Action button */}
      {!showConfirmWithdraw && (
        <div style={{ display: 'flex', gap: '12px' }}>
          {isActive ? (
            <button
              onClick={() => setShowConfirmWithdraw(true)}
              disabled={isProcessing}
              style={{
                flex: 1,
                background: '#fff',
                color: '#dc2626',
                border: '1px solid #fca5a5',
                borderRadius: '12px',
                padding: '14px 24px',
                fontSize: '16px',
                fontWeight: 600,
                cursor: isProcessing ? 'not-allowed' : 'pointer'
              }}
            >
              Odvolať súhlas
            </button>
          ) : (
            <button
              onClick={handleEnableProfiling}
              disabled={isProcessing}
              style={{
                flex: 1,
                background: isProcessing ? '#e5e7eb' : '#667eea',
                color: isProcessing ? '#9ca3af' : '#fff',
                border: 'none',
                borderRadius: '12px',
                padding: '14px 24px',
                fontSize: '16px',
                fontWeight: 600,
                cursor: isProcessing ? 'not-allowed' : 'pointer'
              }}
            >
              {isProcessing ? 'Spracúvam...' : 'Udeliť súhlas'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
